import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useQuery } from '@apollo/react-hooks';

import './mobile-basic-product-list-style.scss';

import NoProduct from '@src/componentsNew/NoProduct/NoProduct';
import MobileHorizontalProductThumbnailSkeleton from '@src/componentsNew/Skeleton/mobile/components/MobileHorizontalProductThumbnailSkeleton';
import MobileProductListSkeleton from '@src/componentsNew/Skeleton/mobile/components/MobileProductListSkeleton';
import MobileHorizontalProductThumbnail from '@src/componentsNew/ProductThumbnail/HorizontalProductThumbnail/MobileHorizontalProductThumbnail';

import { MOBILE_PRODUCT_LIST } from '@src/components/ProductPage/ProductList/queries';
import {
  mobileProducts,
  mobileProducts_products_edges_node,
} from '@components/ProductPage/ProductList/__generated__/mobileProducts';
import { productsVariables } from '@components/ProductPage/ProductList/__generated__/products';

import {
  gaV4GetItemListIdFromInitialVariables,
  gaV4GetItemsFromProducts,
  gaV4ViewItemList,
  getDBIdFromGraphqlId,
} from '@utils';
import { getInvisibleProductIds, MOBILE_PRODUCT_LIST_REQUEST_COUNT,} from '@src/utils/constants';
import { useInvisibleProductIds } from "@src/hooks/useInvisibleProductIds";

interface BasicProductListProps {
  variables: productsVariables;
  initialVariables?: productsVariables; // for GA data
  setNumOfProducts?: Function;
  isSearchResult?: boolean;
  onFirstProductLoaded?: (firstProduct: mobileProducts_products_edges_node) => void;
  onAllProductsLoaded?: () => void;
}

const MobileBasicProductList = ({
  initialVariables,
  variables,
  isSearchResult = false,
  setNumOfProducts,
  onFirstProductLoaded,
  onAllProductsLoaded,
}: BasicProductListProps) => {
  const fetchingRef = useRef(false);
  const hasNextPageRef = useRef(false);
  const prevProductsNum = useRef(0);
  const ready = useInvisibleProductIds();

  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null);

  const [fetchMoreLoading, setFetchMoreLoading] = useState(false);

  const { data, loading, fetchMore } = useQuery<mobileProducts>(MOBILE_PRODUCT_LIST, {
    variables: variables,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const products = data?.products;

      const totalNumOfProducts = products?.totalCount ?? 0;
      if (setNumOfProducts !== undefined) {
        setNumOfProducts(totalNumOfProducts);
      }
      hasNextPageRef.current = products?.pageInfo?.hasNextPage;
      prevProductsNum.current = products?.edges?.length;

      // 첫 번째 상품 정보
      if (products?.edges && products.edges.length > 0) {
        const firstProduct = products.edges[0].node;
        onFirstProductLoaded?.(firstProduct);
      }

      // 모든 상품이 로드되었을 때 호출
      if (!products?.pageInfo?.hasNextPage) {
        onAllProductsLoaded?.();
      }

      const itemListId = gaV4GetItemListIdFromInitialVariables(initialVariables);
      const items = gaV4GetItemsFromProducts(data?.products, itemListId);
      gaV4ViewItemList(items, itemListId);
    },
  });

  // 필터링된 상품 목록 메모이제이션 - 웹(250526)에 보여지지 않는 상품이 있음
  const filteredProducts = useMemo(() => {
    return (
      data?.products?.edges?.filter((edge) => {
          return !getInvisibleProductIds().includes(getDBIdFromGraphqlId(edge.node.id)?.toString());
      }) || []
    );
  }, [data?.products?.edges]);

  const handleFetchMore = useCallback(async () => {
    if (!data?.products?.pageInfo?.endCursor || fetchingRef.current) {
      return;
    }

    fetchingRef.current = true;
    setFetchMoreLoading(true);

    const newVariables = {
      ...variables,
      cursor: data.products.pageInfo.endCursor,
    };

    // 중복 요청 방지
    if (prevProductsNum.current !== data.products.edges.length) {
      fetchingRef.current = false;
      setFetchMoreLoading(false);
      return;
    }

    try {
      await fetchMore({
        variables: newVariables,
        updateQuery: (prevResult: mobileProducts, { fetchMoreResult }) => {
          const products = fetchMoreResult?.products;
          const newEdges = products?.edges;
          const pageInfo = products?.pageInfo;
          const totalCount = products?.totalCount;

          hasNextPageRef.current = pageInfo?.hasNextPage;
          prevProductsNum.current += newEdges?.length || 0;

          if (!pageInfo?.hasNextPage) {
            onAllProductsLoaded?.();
          }

          const itemListId = gaV4GetItemListIdFromInitialVariables(initialVariables);
          const items = gaV4GetItemsFromProducts(products, itemListId);
          gaV4ViewItemList(items, itemListId);

          return newEdges?.length
            ? {
                products: {
                  __typename: prevResult.products.__typename,
                  edges: [...prevResult.products.edges, ...newEdges],
                  pageInfo,
                  totalCount,
                },
              }
            : prevResult;
        },
      });
    } catch (error) {
      console.error('Error fetching more products:', error);
    } finally {
      fetchingRef.current = false;
      setFetchMoreLoading(false);
    }
  }, [data, fetchMore, variables, initialVariables, onAllProductsLoaded]);

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (!filteredProducts.length || !hasNextPageRef.current) {
      return;
    }

    observerRef.current = new IntersectionObserver(
      async (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasNextPageRef.current) {
          await handleFetchMore();
        }
      },
      { threshold: 1.0 },
    );

    if (loadMoreTriggerRef.current) {
      observerRef.current.observe(loadMoreTriggerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [filteredProducts.length, hasNextPageRef.current, handleFetchMore]);

  if (loading || !ready) {
    return <MobileProductListSkeleton type="vertical" />;
  }

  return data?.products?.totalCount > 0 ? (
    <div id="mobile-product-list-wrapper">
      {filteredProducts.map((edge, idx) => (
        <div key={edge.node.id} className="mobile-list-load-trigger-wrapper">
          <MobileHorizontalProductThumbnail product={edge.node} />

          {idx === filteredProducts.length - 2 && hasNextPageRef.current && (
            <div ref={loadMoreTriggerRef} className="mobile-list-load-trigger" />
          )}
        </div>
      ))}

      {fetchMoreLoading &&
        Array.from({ length: MOBILE_PRODUCT_LIST_REQUEST_COUNT }).map((_, index) => (
          <MobileHorizontalProductThumbnailSkeleton key={index} />
        ))}
    </div>
  ) : (
    <div className={`mobile-no-product-container ${isSearchResult ? 'search-result' : ''}`}>
      <NoProduct isSearchResult={isSearchResult} />
    </div>
  );
};

export default MobileBasicProductList;
